import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icon';
import { TileIconButton } from './tile-icon-button';

interface SideSheetProps {
  open: boolean;
  onClose: () => void;
  kicker?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Pinned action row at the bottom (save / cancel). */
  footer?: ReactNode;
  width?: number;
  children: ReactNode;
}

/** Right-hand sheet for details and forms (location detail, targets, user form). */
export function SideSheet({
  open,
  onClose,
  kicker,
  title,
  subtitle,
  footer,
  width = 480,
  children,
}: SideSheetProps) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width,
            maxWidth: '100vw',
            background: theme.vars.palette.md.panel,
            borderLeft: `1px solid ${theme.vars.palette.md.border}`,
            boxShadow: theme.vars.palette.md.shadowLg,
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: '18px 20px 16px',
          borderBottom: `1px solid ${theme.vars.palette.md.border}`,
        })}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {kicker && (
            <Typography variant="kicker" component="div">
              {kicker}
            </Typography>
          )}
          <Typography variant="h2" component="h2" sx={{ mt: kicker ? '4px' : 0 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <TileIconButton label={t('actions.close')} onClick={onClose} size={38}>
          <Icon name="x" size={18} />
        </TileIconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: '18px 20px 24px' }}>{children}</Box>
      {footer && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            p: '14px 20px',
            borderTop: `1px solid ${theme.vars.palette.md.border}`,
            background: theme.vars.palette.md.panel2,
          })}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
}
